import React, { useRef, useState } from "react";
import AppButton from "../layout/AppButton";
import { useOnboarding } from "../../hooks/useOnboarding";

/**
 * Minimal text-to-DAX chat for an onboarded dashboard.
 *
 * Calls POST /api/onboarding/chat with { reportGuid, question }; the backend
 * (services/onboarding_chat.py) answers using only the persisted onboarding
 * grounding and a live DAX query — the enterprise engine is not involved.
 * Shown on the wizard's completion screen to prove the loop is closed.
 */
const OnboardingChat = ({ reportGuid, reportName, suggestedQuestions = [] }) => {
  const ob = useOnboarding();
  const [input, setInput] = useState("");
  const [turns, setTurns] = useState([]); // { role: "user"|"bot", text, dax?, rowCount? }
  const listRef = useRef(null);

  const send = async () => {
    const q = input.trim();
    if (!q || ob.busy) return;
    setInput("");
    setTurns((t) => [...t, { role: "user", text: q }]);
    try {
      const data = await ob.chat(reportGuid, q);
      setTurns((t) => [...t, {
        role: "bot",
        text: data.answer || "(no answer)",
        dax: data.dax,
        rowCount: data.rowCount,
      }]);
    } catch {
      setTurns((t) => [...t, { role: "bot", text: "Something went wrong answering that." }]);
    } finally {
      requestAnimationFrame(() => listRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }));
    }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <div className="ob-chat">
      <div className="ob-chat-head">Ask {reportName || "this dashboard"}</div>
      <div className="ob-chat-list" ref={listRef}>
        {!turns.length && (
          <div style={{ padding: 18 }}>
            <div className="ob-empty" style={{ marginBottom: suggestedQuestions.length ? 12 : 0 }}>
              {suggestedQuestions.length ? "Try one of these questions:" : 'Try: "What are total sales by region?"'}
            </div>
            {suggestedQuestions.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {suggestedQuestions.map((q, i) => (
                  <button
                    key={i}
                    className="ob-suggested-q"
                    onClick={() => { setInput(q); }}
                    style={{
                      background: "#f3f0ff", border: "1px solid #c4b5fd", borderRadius: 16,
                      padding: "6px 14px", fontSize: 13, color: "#5b21b6", cursor: "pointer",
                      textAlign: "left", lineHeight: 1.3,
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`ob-bubble ${t.role}`}>
            <div className="ob-bubble-text">{t.text}</div>
            {t.dax && (
              <details className="ob-bubble-dax">
                <summary>DAX{typeof t.rowCount === "number" ? ` · ${t.rowCount} rows` : ""}</summary>
                <pre>{t.dax}</pre>
              </details>
            )}
          </div>
        ))}
        {ob.busy === "chat" && <div className="ob-bubble bot"><span className="ob-spin" />Thinking…</div>}
      </div>
      <div className="ob-chat-input">
        <input
          className="ob-input"
          style={{ flex: 1 }}
          placeholder="Ask a question about this dashboard…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          disabled={ob.busy === "chat"}
        />
        <AppButton variant="fill" disabled={!input.trim() || ob.busy === "chat"} onClick={send}>Send</AppButton>
      </div>
    </div>
  );
};

export default OnboardingChat;
